'use client';
import type {
  KanbanColumn,
  KanbanFeature,
  KanbanOwner,
  KanbanStats,
} from '@/types/kanban';
import {
  candidateStages,
  candidateStagesById,
  getCandidateStageCategory,
  getStageByAssociationTypeId,
  type CandidateStatusId,
  type StageAssociationLookup,
} from '@/types/candidate';
import type {
  CandidateAssociationTag,
  VacancyApplication,
  VacancyCompany,
} from '@/types/vacancy';
import { combineAssociationTags } from '@/lib/tag-utils';

export const columns: KanbanColumn[] = candidateStages.map((stage) => ({
  id: stage.id,
  name: stage.label,
  color: stage.color,
  indicatorClass: stage.indicatorClass,
}));

const DEFAULT_STATUS: CandidateStatusId = 'sollicitanten';

const STATUS_ALIASES: Partial<Record<string, CandidateStatusId>> = {
  sollicitant: 'sollicitanten',
  sollicitanten: 'sollicitanten',
  gesolliciteerd: 'sollicitanten',
  applied: 'sollicitanten',
  screening: 'uitnodigingen',
  uitnodigen: 'uitnodigingen',
  uitnodiging: 'uitnodigingen',
  uitnodigingen: 'uitnodigingen',
  interview: 'gesprek-sw',
  interviewing: 'gesprek-sw',
  'gesprek-sw': 'gesprek-sw',
  'gesprek-s&w': 'gesprek-sw',
  'gesprek-s-w': 'gesprek-sw',
  'gesprek s&w': 'gesprek-sw',
  'gesprek sw': 'gesprek-sw',
  'gesprek opdrachtgever': 'gesprek-opdrachtgever',
  'gesprek-opdrachtgever': 'gesprek-opdrachtgever',
  'eerste gesprek opdrachtgever': 'gesprek-opdrachtgever',
  'tweede gesprek opdrachtgever': 'gesprek-opdrachtgever',
  'derde gesprek': 'gesprek-opdrachtgever',
  'bespreken opdrachtgever': 'gesprek-opdrachtgever',
  assessment: 'assessment',
  offered: 'assessment',
  offer: 'assessment',
  aangenomen: 'aangenomen',
  hired: 'aangenomen',
  accepted: 'aangenomen',
  accept: 'aangenomen',
  afgewezen: 'afgewezen',
  rejected: 'afgewezen',
  reject: 'afgewezen',
  withdrawn: 'teruggetrokken',
  'afgewezen op cv': 'afgewezen',
  'afgewezen op arbeidsvoorwaarden': 'afgewezen',
  'afgewezen na gesprek s&w': 'afgewezen',
  'afgewezen door opdrachtgever': 'afgewezen',
  'uitgebreid afwijzen op cv': 'afgewezen',
  'nog afwijzen door office': 'afgewezen',
  benaderd: 'sollicitanten',
  'reserve kandidaat': 'sollicitanten',
  'kandidaat teruggetrokken': 'teruggetrokken',
  'arbeidvoordwaardengesprek': 'assessment',
};

const normalizeStatusKey = (value?: string | null) => {
  if (!value) {
    return null;
  }
  return value.trim().toLowerCase();
};

const resolveOwner = (company?: VacancyCompany | null): KanbanOwner => ({
  id: company?.id ?? 'company-unknown',
  name: company?.name ?? 'Onbekend bedrijf',
});

const mapStatusToColumn = (status?: string | null): CandidateStatusId => {
  const raw = normalizeStatusKey(status);
  if (!raw) {
    return DEFAULT_STATUS;
  }

  const hyphenated = raw.replace(/\s+/g, '-');

  const directMatch = candidateStages.find(
    (stage) => stage.id === raw || stage.id === hyphenated
  );
  if (directMatch) {
    return directMatch.id;
  }

  return STATUS_ALIASES[hyphenated] ?? STATUS_ALIASES[raw] ?? DEFAULT_STATUS;
};

const isValidDate = (value: Date | number) => {
  const timestamp = value instanceof Date ? value.getTime() : value;
  return Number.isFinite(timestamp);
};

const parseDate = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return isValidDate(parsed) ? parsed : null;
};

const splitName = (
  name?: string | null
): { firstName: string; lastName: string } => {
  if (!name) {
    return { firstName: 'Onbekende', lastName: 'kandidaat' };
  }
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) {
    return { firstName: 'Onbekende', lastName: 'kandidaat' };
  }
  const [firstName, ...rest] = parts;
  return {
    firstName,
    lastName: rest.join(' '),
  };
};

export const normalizeHubspotId = (value?: string | number | null): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const stringValue = String(value).trim();
  if (!stringValue) {
    return null;
  }
  if (/^\d+$/.test(stringValue)) {
    return stringValue;
  }

  const separators = ['=', '-', ':', '|', '/'];
  for (const separator of separators) {
    const segment = stringValue.split(separator).pop();
    if (segment && /^\d+$/.test(segment)) {
      return segment;
    }
  }

  const digitsOnly = stringValue.replace(/\D+/g, '');
  return digitsOnly ? digitsOnly : null;
};

type MapApplicationsOptions = {
  fallbackVacancyId?: string | null;
  stageAssociations?: StageAssociationLookup;
};

export const mapApplicationsToKanbanFeatures = (
  applications: VacancyApplication[],
  company?: VacancyCompany | null,
  options: MapApplicationsOptions = {}
): KanbanFeature[] => {
  const ownerTemplate = resolveOwner(company);
  const fallbackVacancyId = normalizeHubspotId(
    options.fallbackVacancyId ?? company?.id ?? null
  );
  const stageAssociations = options.stageAssociations;
  const associationStageByTypeId =
    stageAssociations && Object.keys(stageAssociations).length > 0
      ? (() => {
          const map = new Map<string, CandidateStatusId>();
          Object.values(stageAssociations).forEach((association) => {
            if (!association?.typeId) {
              return;
            }
            map.set(association.typeId, association.stageId);
          });
          return map;
        })()
      : null;
  return applications.map((application) => {
    const candidate = application.candidate ?? null;
    const candidateName =
      candidate?.name ?? candidate?.email ?? 'Onbekende kandidaat';
    const { firstName, lastName } = splitName(candidateName);
    const contactId =
      normalizeHubspotId(candidate?.id) ??
      normalizeHubspotId(application.candidateId) ??
      normalizeHubspotId(application.applicationId) ??
      null;
    const vacancyId =
      normalizeHubspotId(application.vacancyId) ?? fallbackVacancyId ?? null;
    const startAt =
      parseDate(application.appliedAt) ??
      parseDate(application.updatedAt) ??
      parseDate(application.lastSyncedAt) ??
      parseDate(application.hubspotUpdatedAt) ??
      new Date();

    const endAt = parseDate(application.lastSyncedAt) ?? startAt;

    const typeId = application.associationTypeId ?? null;
    const stageIdFromAssociations =
      typeId && associationStageByTypeId ? associationStageByTypeId.get(typeId) : undefined;
    const stageFromType = stageIdFromAssociations
      ? candidateStagesById.get(stageIdFromAssociations)
      : getStageByAssociationTypeId(typeId);
    const fallbackColumn = mapStatusToColumn(application.status);
    const columnId = stageFromType?.id ?? fallbackColumn;
    const stageAssociation = stageAssociations?.[columnId] ?? stageAssociations?.[fallbackColumn];
    const combinedTags = combineAssociationTags(application.tagsJson, application.tags);
    const associationTags: CandidateAssociationTag[] | null =
      combinedTags.length > 0
        ? combinedTags.map((tag) => ({
            slug: tag.slug,
            label: tag.label,
          }))
        : null;
    const resolvedLabel =
      candidateStagesById.get(columnId)?.label ??
      candidateStagesById.get(fallbackColumn)?.label ??
      stageFromType?.hubspotLabel ??
      stageAssociation?.label ??
      application.statusLabel ??
      application.status;

    return {
      id:
        candidate?.id ??
        application.candidateId ??
        application.applicationId,
      applicationId: application.applicationId ?? null,
      vacancyId,
      contactId,
      associationTypeId: typeId ?? stageAssociation?.typeId ?? null,
      statusLabel: resolvedLabel,
      firstName,
      lastName,
      phone: candidate?.phone ?? candidate?.mobilePhone ?? null,
      email: candidate?.email ?? '',
      birthDate: candidate?.birthDate ?? null,
      city: candidate?.city ?? null,
      street: candidate?.street ?? null,
      postalCode: candidate?.postalCode ?? null,
      resumeUrl: null,
      hubspotUrl: null,
      name: candidateName,
      startAt,
      endAt,
      column: columnId,
      owner: { ...ownerTemplate },
      associationTags,
      tags: [],
      isStatusOverridePending: false,
    };
  });
};

export const createInitialKanbanFeatures = (): KanbanFeature[] => [];

export const cloneFeatures = (data: KanbanFeature[]): KanbanFeature[] =>
  data.map((feature) => ({
    ...feature,
    owner: { ...feature.owner },
  }));

export const calculateKanbanStats = (data: KanbanFeature[]): KanbanStats => {
  const kandidaten = data.filter((feature) => {
    const category = getCandidateStageCategory(feature.column);
    return category !== 'aangenomen' && category !== 'afgewezen';
  }).length;
  const inGesprek = data.filter((feature) => {
    const category = getCandidateStageCategory(feature.column);
    return category === 'interview';
  }).length;
  const aangenomen = data.filter((feature) => {
    const category = getCandidateStageCategory(feature.column);
    return category === 'aangenomen';
  }).length;
  const afgewezen = data.filter((feature) => {
    const category = getCandidateStageCategory(feature.column);
    return category === 'afgewezen';
  }).length;
  return { kandidaten, inGesprek, aangenomen, afgewezen };
};
